import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useMemo, type ReactNode } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { articleSchema } from '@/lib/seo';
import { mainSiteUrl } from '@/mocks/site';
import { articleMetas } from '@/mocks/articlesMeta';

export default function ArticlePage() {
  const params = useParams<{ slug?: string }>();
  const { t } = useTranslation();

  // Support both /usfans-legit (old route) and /articles/:slug (new route)
  const slug = params.slug || 'usfans-legit';
  const meta = useMemo(() => articleMetas.find((a) => a.slug === slug), [slug]);

  const prefix = `article_${slug}`;

  const toc = [
    t(`${prefix}_toc_1`),
    t(`${prefix}_toc_2`),
    t(`${prefix}_toc_3`),
    t(`${prefix}_toc_4`),
    t(`${prefix}_toc_5`),
  ].filter((v) => !v.startsWith('article_'));

  const takeaways = [
    t(`${prefix}_takeaways_1`),
    t(`${prefix}_takeaways_2`),
    t(`${prefix}_takeaways_3`),
    t(`${prefix}_takeaways_4`),
  ].filter((v) => !v.startsWith('article_'));

  const verifyList = [
    t(`${prefix}_s3_li_1`),
    t(`${prefix}_s3_li_2`),
    t(`${prefix}_s3_li_3`),
  ].filter((v) => !v.startsWith('article_'));

  const warningList = [
    t(`${prefix}_s4_li_1`),
    t(`${prefix}_s4_li_2`),
    t(`${prefix}_s4_li_3`),
  ].filter((v) => !v.startsWith('article_'));

  const checklist = [
    t(`${prefix}_s5_li_1`),
    t(`${prefix}_s5_li_2`),
    t(`${prefix}_s5_li_3`),
  ].filter((v) => !v.startsWith('article_'));

  const articleFaqs = [
    { q: t(`${prefix}_faq_q1`), a: t(`${prefix}_faq_a1`) },
    { q: t(`${prefix}_faq_q2`), a: t(`${prefix}_faq_a2`) },
    { q: t(`${prefix}_faq_q3`), a: t(`${prefix}_faq_a3`) },
  ].filter((f) => !f.q.startsWith('article_') && !f.a.startsWith('article_'));

  const titleText = t(`${prefix}_title`);
  const subtitleText = t(`${prefix}_subtitle`);
  const heroImage = meta?.image || 'https://readdy.ai/api/search-image?query=Modern%20minimal%20office%20desk%20with%20laptop%20and%20coffee%20cup%2C%20soft%20natural%20window%20light%2C%20clean%20workspace%2C%20editorial%20photography&width=1600&height=700&seq=article-default-hero&orientation=landscape';

  const canonicalPath = slug === 'usfans-legit' ? '/usfans-legit' : `/articles/${slug}`;
  const articleJsonLd = articleSchema({
    path: canonicalPath,
    headline: titleText,
    description: subtitleText,
    image: heroImage,
    dateModified: '2026-08-23',
  });

  return (
    <div className="bg-background-50">
      <JsonLd data={articleJsonLd} />
      <Navbar />

      <section className="relative flex min-h-[380px] w-full items-center justify-center overflow-hidden">
        <img
          src={heroImage}
          alt={titleText}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/65"></div>
        <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-20 text-center md:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
          >
            <i className="ri-arrow-left-line"></i>
            {t('back_home')}
          </Link>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-white md:text-4xl">
            {titleText}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/85">
            {subtitleText}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-sm text-white/75">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {t(`${prefix}_updated`)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {t(`${prefix}_reading_time`)}
            </span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-6">
              <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary-700">
                <i className="ri-lightbulb-flash-line"></i>
                {t(`${prefix}_short_answer_label`)}
              </span>
              <p className="mt-3 text-sm leading-relaxed text-foreground-800 md:text-base">
                {t(`${prefix}_short_answer`)}
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_intro_p1`)}</p>
              <p className="text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_intro_p2`)}</p>
            </div>

            <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">{t(`${prefix}_takeaways_title`)}</h2>
              <ul className="mt-4 space-y-2.5">
                {takeaways.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <SectionHeading>{t(`${prefix}_s1_title`)}</SectionHeading>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s1_p1`)}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s1_p2`)}</p>

            <SectionHeading>{t(`${prefix}_s2_title`)}</SectionHeading>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s2_p1`)}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s2_p2`)}</p>
            {!t(`${prefix}_s2_p3`).startsWith('article_') && (
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s2_p3`)}</p>
            )}

            <SectionHeading>{t(`${prefix}_s3_title`)}</SectionHeading>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s3_p1`)}</p>
            {verifyList.length > 0 && (
              <ul className="mt-4 space-y-2.5">
                {verifyList.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-arrow-right-circle-line mt-0.5 text-primary-500"></i>
                    {item}
                  </li>
                ))}
              </ul>
            )}

            <SectionHeading>{t(`${prefix}_s4_title`)}</SectionHeading>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s4_p1`)}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s4_p2`)}</p>
            {warningList.length > 0 && (
              <ul className="mt-4 space-y-2.5">
                {warningList.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-alert-line mt-0.5 text-accent-600"></i>
                    {item}
                  </li>
                ))}
              </ul>
            )}

            <SectionHeading>{t(`${prefix}_s5_title`)}</SectionHeading>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s5_p1`)}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s5_p2`)}</p>
            {!t(`${prefix}_s5_p3`).startsWith('article_') && (
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s5_p3`)}</p>
            )}
            {checklist.length > 0 && (
              <>
                <SectionHeading>{t(`${prefix}_s6_title`)}</SectionHeading>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{t(`${prefix}_s6_p1`)}</p>
                <ul className="mt-4 space-y-2.5">
                  {checklist.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="mt-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{t(`${prefix}_faq_title`)}</h2>
              <div className="mt-6 space-y-4">
                {articleFaqs.map((faq) => (
                  <div key={faq.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h3 className="text-sm font-bold text-foreground-950 md:text-base">{faq.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12 rounded-lg border border-primary-200 bg-primary-50 p-6">
              <h2 className="font-heading text-xl font-bold text-foreground-950">{t(`${prefix}_conclusion_title`)}</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{t(`${prefix}_conclusion_p1`)}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{t(`${prefix}_conclusion_p2`)}</p>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                {t(`${prefix}_conclusion_cta`)}
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            <div className="mt-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                <i className="ri-arrow-left-line"></i>
                {t('back_home')}
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">{t(`${prefix}_toc_title`)}</h3>
              <ol className="mt-4 space-y-2.5">
                {toc.map((item, index) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-600">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-background-100 text-xs font-bold text-primary-600">
                      {index + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ol>
              <div className="mt-6 border-t border-background-200 pt-6">
                <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                  <i className="ri-arrow-left-line"></i>
                  {t('back_home')}
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">{children}</h2>;
}